import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button, Space, Typography, Modal, Form, Spin,
  Input, Select, Row, Col, message, Alert, Popconfirm, Switch, Tooltip,
} from 'antd';
import {
  ReloadOutlined, VideoCameraOutlined, DeleteOutlined,
} from '@ant-design/icons';
// Dọn UI 260713: 1 màn liền mạch — EventPlanTab chứa stepper + editor; Bank & Ghép phim gộp vào drawer trong đó.
import EventPlanTab from './EventPlanTab';
import { planOpenTargetOf, workflowPath } from './workflowBoardUtils'; // 261010 P2a: nút "Mở khung mới" (plan Sky)
import { buildGenEventPlanOpts, SKY_FLOW_DEFAULT, SKY_TOGGLE_TOOLTIP } from './skyVideoMetaUtils'; // 261010 P1a
import { DEFAULT_GENERATION_FORM, GENERATION_ENGINES, GENERATION_MODELS_20, GENERATION_RESOLUTIONS, generationFormFixOf } from './workflowGenerationUtils'; // 261010 UI2
import { chapterOptionsOf } from './filmChapterOptions'; // 261010 UI2-SCRIPT
import { CREATE_PLAN_LABELS } from './filmCreatePlanLabels'; // 261010 SOTAIL (L39)
import { deletePlanErrorTextOf } from './filmCreateChapterUtils'; // 261030: câu lỗi xoá plan = câu tiếng Việt của BE
import { FilmPlanProvider, useFilmPlan } from './FilmPlanContext';
import { filmApi } from '../../services/api/film';
import { sourceStoriesApi } from '../../services/api';
import type { FilmPlan, FilmPlanStatus, FilmRunningJob, FilmFailedJob, FilmPromptMode } from '../../types/film';
import { apiErrorText, isServerStillWorking } from './apiErrorText';
import { loadDismissedJobs, addDismissedJobs } from './dismissedJobsStore';
import type { SourceChapter } from '../../types';
import './storyboard.css';
import WfIcon from './WfIcon';
import { planStatusIconOf } from './workflowStatusIcons';
import './storyPage.css';

const { Text } = Typography;

// Plan-card (Phase 1): nhãn trạng thái thân thiện + % tiến độ suy từ status (list không kèm số event).
const STATUS_META: Record<FilmPlanStatus, { label: string; pct: number; done?: boolean }> = {
  DRAFT: { label: 'Draft', pct: 12 },
  PLAN_APPROVED: { label: 'Plan ready', pct: 30 },
  FRAMES_PENDING: { label: 'Preparing', pct: 45 },
  FRAMES_APPROVED: { label: 'Refs ready', pct: 58 },
  VIDEO_PENDING: { label: 'Generating', pct: 74 },
  VIDEO_APPROVED: { label: 'Clips ready', pct: 90 },
  ASSEMBLED: { label: 'Assembled', pct: 100, done: true },
};
// poster gradient điện ảnh, ổn định theo tên/id plan
const posterBg = (seed: string) => {
  let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `radial-gradient(120% 100% at 35% 0%, hsl(${h} 44% 60%), hsl(${(h + 28) % 360} 40% 38%) 58%, hsl(${(h + 55) % 360} 38% 20%))`;
};

// ── Plan List ──
const PlanList: React.FC<{ storyId: string; onSelect: (plan: FilmPlan) => void; onCreateFour: () => void; active?: boolean }> = ({ storyId, onSelect, onCreateFour, active }) => {
  const { plans, loadingPlans, reloadPlans, selectPlan } = useFilmPlan();
  const [chapters, setChapters] = useState<SourceChapter[]>([]);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Việc nền cấp STORY (hiện chỉ có "tạo plan") — server nói, không phải tab đoán: sống qua F5, thấy cả lượt
  // do tab khác bấm. `waitingPlan` = vừa bấm tạo, đang chờ plan mới xuất hiện trong danh sách.
  const [storyJobs, setStoryJobs] = useState<FilmRunningJob[]>([]);
  const [storyFailures, setStoryFailures] = useState<FilmFailedJob[]>([]);
  const [dismissedJobs, setDismissedJobs] = useState<string[]>(() => loadDismissedJobs());
  const [waitingPlan, setWaitingPlan] = useState(false);

  const loadChapters = useCallback(async () => {
    try {
      const res = await sourceStoriesApi.getSourceChapters(storyId, { pageSize: 500 });
      setChapters(((res as any).data || []).sort((a: SourceChapter, b: SourceChapter) => a.orderIndex - b.orderIndex));
    } catch (e: any) { message.error(CREATE_PLAN_LABELS.chaptersLoadFailed(e?.message)); }
  }, [storyId]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await filmApi.getStoryFilmJobs(storyId);
      setStoryJobs(r.jobs || []);
      setStoryFailures(r.failures || []);
      return r.jobs || [];
    } catch { return []; /* im lặng: thiếu băng job không đáng làm hỏng cả màn */ }
  }, [storyId]);

  useEffect(() => { loadChapters(); }, [loadChapters]);
  useEffect(() => { if (active) loadChapters(); }, [active, loadChapters]);
  useEffect(() => { if (modalOpen) loadChapters(); }, [modalOpen, loadChapters]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Đang tạo plan → hỏi dồn (5s) và reload danh sách để plan mới tự hiện; rảnh thì thưa (30s).
  // ⚠️ Poll phải IM LẶNG (`silent`): bật `loadingPlans` mỗi 5 giây làm cả grid nhảy sang spinner rồi vẽ lại
  // → trang "nháy liên tục" (user báo ngay lượt thử đầu).
  const creating = storyJobs.some((j) => j.type === 'FILM_GEN_PLAN');
  useEffect(() => {
    const t = setInterval(async () => {
      const jobs = await loadJobs();
      if (creating || waitingPlan) await reloadPlans({ silent: true });
      // Job tạo plan đã xong (không còn trong danh sách đang chạy) → thôi chờ.
      if (waitingPlan && !jobs.some((j) => j.type === 'FILM_GEN_PLAN')) setWaitingPlan(false);
    }, (creating || waitingPlan) ? 5000 : 30000);
    return () => clearInterval(t);
  }, [loadJobs, reloadPlans, creating, waitingPlan]);

  // Gộp các lần chết GIỐNG NHAU thành một băng (cùng EventPlanTab): cửa sổ failures là 180 phút nên sau khi
  // sửa xong nguyên nhân, cả xâu lỗi lịch sử vẫn xếp chồng và người dùng tưởng còn lỗi.
  const groupedFailures = useMemo(() => {
    const live = storyFailures.filter((f) => !dismissedJobs.includes(f.id));
    const byKey = new Map<string, { key: string; note: string; minutesAgo: number; count: number; ids: string[] }>();
    for (const f of live) {
      const key = `${f.type}|${f.note}`;
      const g = byKey.get(key);
      if (g) { g.count += 1; g.ids.push(f.id); g.minutesAgo = Math.min(g.minutesAgo, f.minutesAgo); }
      else byKey.set(key, { key, note: f.note, minutesAgo: f.minutesAgo, count: 1, ids: [f.id] });
    }
    return Array.from(byKey.values()).sort((a, b) => a.minutesAgo - b.minutesAgo).slice(0, 3);
  }, [storyFailures, dismissedJobs]);

  const usedChapterIds = new Set(plans.flatMap((p) => p.sourceChapterIds || []));
  const chapterLabel = (id: string) => {
    const c = chapters.find((x) => x.id === id);
    return c ? `#${c.orderIndex}${c.name ? ` ${c.name}` : ''}` : id.slice(0, 6);
  };
  const watchedFrom = Form.useWatch('fromChapterId', form);
  const watchedSky = Form.useWatch('skyFlow', form) ?? SKY_FLOW_DEFAULT; // UI2: engine chỉ bật khi luồng mới BẬT
  const watchedEngine = Form.useWatch('genEngine', form);
  const fromOrder = chapters.find((c) => c.id === watchedFrom)?.orderIndex ?? -1;
  // Options dựng một lần (không tạo mảng mới mỗi lần vẽ lại do poll/Form.useWatch) — danh sách đang mở không bị dựng lại giữa lúc bấm chuột.
  const fromOptions = useMemo(() => chapterOptionsOf(chapters, usedChapterIds, -1), [chapters, plans]); // eslint-disable-line react-hooks/exhaustive-deps
  const toOptions = useMemo(() => chapterOptionsOf(chapters, usedChapterIds, fromOrder), [chapters, plans, fromOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // 260927 (phiên test luồng): bỏ ô "Kiểu prompt" khỏi modal tạo plan — lúc tạo plan mode chưa có tác dụng gì (chỉ
  // dùng ở bước dựng prompt, sau Bank) và người dùng chưa có căn cứ chọn. Plan mới luôn Tự nhiên (ý đội content, L1);
  // đổi mode cả plan / từng event ở bước Prompts. PHẢI gửi tường minh: thiếu field thì BE mặc định TECHNICAL.
  // Dropdown vẽ NGAY TRONG ô của hộp (không portal ra <body>): bấm chuột vào option luôn nằm trong cây DOM của hộp, không bị lớp phủ / bẫy focus của Modal chen vào.
  const inField = (node: HTMLElement): HTMLElement => node.parentElement ?? document.body;

  const NEW_PLAN_PROMPT_MODE: FilmPromptMode = 'NATURAL';

  const onCreate = async () => {
    const values = await form.validateFields();
    const from = chapters.find((c) => c.id === values.fromChapterId);
    const to = chapters.find((c) => c.id === values.toChapterId);
    if (!from || !to) return;
    const [lo, hi] = [from.orderIndex, to.orderIndex].sort((a, b) => a - b);
    const ids = chapters.filter((c) => c.orderIndex >= lo && c.orderIndex <= hi && !usedChapterIds.has(c.id)).map((c) => c.id);
    if (!ids.length) { message.warning(CREATE_PLAN_LABELS.allUsedWarning); return; }
    setModalOpen(false);
    setGenerating(true);
    try {
      // 🔴 260806: tạo plan nay chạy NỀN (202). Trước đây endpoint đồng bộ, mà pass này > 180s trong khi
      // gateway cắt đúng ở 180s ⇒ client nhận 504 dù server vẫn tạo plan xong ⇒ UI mất dấu, người dùng bấm
      // lại và sinh plan trùng (đo trên prod: 3 plan trong 5 phút). Nay: bấm → 202 → băng "đang tạo" +
      // poll danh sách, khoá ở server chặn cú bấm thứ hai.
      // 261010 P1a: công tắc "Luồng Sky" TẮT ⇒ request y hệt trước đây (không có `flowVersion`).
      // 261030: mọi plan tạo từ bề mặt mới là layout four; generation là contract bắt buộc của BE.
      const opts = { ...buildGenEventPlanOpts(values, NEW_PLAN_PROMPT_MODE), flowVersion: 'sky' as const, workflowLayout: 'four' as const };
      await filmApi.genEventPlan(storyId, ids, opts);
      message.info('Đang tạo plan ở nền (~1–3 phút) — danh sách sẽ tự hiện khi xong, đừng bấm lại.');
      form.resetFields();
      setWaitingPlan(true);
      loadJobs();
    } catch (e: any) {
      // 409 = đã có lượt tạo đang chạy; 504 = bị cắt nhưng server vẫn chạy → cả hai đều KHÔNG được nói "thất bại".
      message.error(apiErrorText(e, 'Tạo plan'));
      if (isServerStillWorking(e) || e?.response?.status === 409) { setWaitingPlan(true); loadJobs(); }
      await reloadPlans({ silent: true });
    } finally { setGenerating(false); }
  };

  const handleDelete = async (planId: string) => {
    setDeleting(planId);
    try {
      await filmApi.deletePlan(planId);
      message.success('Đã xoá plan.');
      selectPlan(undefined);
      await reloadPlans();
    } catch (e: any) {
      // 409 WORKFLOW_RUN_ACTIVE / UNKNOWN_NEEDS_MANUAL_RECONCILE…: hiện đúng câu của BE. 404 = đã bị xoá ở nơi khác ⇒ tải lại danh sách.
      message.error(deletePlanErrorTextOf(e));
      if (e?.response?.status === 404) await reloadPlans({ silent: true });
    }
    finally { setDeleting(null); }
  };

  return (
    <div className="sb-scope sp-pane">
      {/* Nút làm mới nằm cùng dòng với băng trạng thái (bỏ hàng tiêu đề «Film Plans» trùng tên tab). */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
      {/* Việc nền cấp story: server nói, sống qua F5. Thay cho băng cũ dựa vào `generating` của tab này —
          băng cũ biến mất ngay khi request bị gateway cắt, đúng lúc việc vẫn đang chạy. */}
      {(creating || waitingPlan) && (
        <Alert style={{ marginBottom: 8 }} type="info" showIcon icon={<Spin size="small" />}
          message={creating
            ? `Đang tạo plan ở nền — ${storyJobs.find((j) => j.type === 'FILM_GEN_PLAN')?.runningMinutes ?? 0}′. Danh sách tự hiện khi xong, đừng bấm lại (mỗi lượt là một lần tính tiền).`
            : 'Đang chờ plan mới xuất hiện…'} />
      )}
      {groupedFailures.map((g) => (
        <Alert key={g.key} type="error" showIcon closable style={{ marginBottom: 12 }}
          onClose={() => setDismissedJobs(addDismissedJobs(g.ids))}
          message={`Tạo plan thất bại${g.count > 1 ? ` ${g.count} lần` : ''} — gần nhất ${g.minutesAgo}′ trước`}
          description={<span style={{ fontSize: 12 }}>{g.note}</span>} />
      ))}
        </div>
        <Tooltip title="Tải lại danh sách plan"><Button icon={<ReloadOutlined />} size="small" type="text" aria-label="Tải lại danh sách plan" onClick={() => reloadPlans()} /></Tooltip>
      </div>
      {loadingPlans
        ? <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
        : (
          <div className="sb-plans-grid">
            {/* Ô «Plan mới» luôn là ô ĐẦU của lưới (User 261030) — không phải trôi xuống cuối khi có nhiều plan. */}
            <div className="sb-newplan" onClick={onCreateFour}>
              <div><div className="plus">＋</div>Plan mới<br /><span style={{ fontSize: 11, fontFamily: 'var(--sb-mono)', color: 'var(--ink-3)' }}>từ các chương</span></div>
            </div>
            {plans.map((p) => {
              const m = STATUS_META[p.status];
              return (
                <div key={p.id} className="sb-plan" onClick={(e) => {
                  // Click XUẤT PHÁT từ vùng xoá (.del: nút thùng rác / popconfirm) → KHÔNG mở plan.
                  // Guard theo target thay vì chỉ stopPropagation vì AntD Popconfirm clone lại onClick con
                  // (stopPropagation dễ bị nuốt) → trước đây bấm Delete vẫn nhảy vào plan.
                  if ((e.target as HTMLElement).closest('.del')) return;
                  selectPlan(p.id); onSelect(p);
                }}>
                  <div className="sb-poster" style={{ background: posterBg(p.name || p.id) }}>
                    <div className="grain" /><div className="shade" />
                    <div className="st"><Tooltip title={m.label}><span className={`sb-stpill${m.done ? ' done' : ''}`} role="img" aria-label={m.label} tabIndex={0}><WfIcon name={planStatusIconOf(p.status).icon} /></span></Tooltip></div>
                    {/* stopPropagation CHỈ ở div bọc (click trong vùng xoá, kể cả hộp xác nhận vẽ qua portal, không lọt ra row
                        onClick = mở plan). 🔴 KHÔNG đặt stopPropagation trên chính nút: Popconfirm gắn handler mở hộp ở phần tử
                        bọc nút (span bên dưới) — nút chặn nổi bọt thì handler đó không chạy ⇒ hộp không mở ⇒ không gọi DELETE (User báo 261030). */}
                    <div className="del" onClick={(e) => e.stopPropagation()}>
                      <Popconfirm title="Xoá plan này?" description="Toàn bộ cảnh và shot của plan sẽ bị xoá, không khôi phục được." onConfirm={() => handleDelete(p.id)} okText="Xoá" cancelText="Huỷ" okButtonProps={{ danger: true }}>
                        <span style={{ display: 'inline-flex' }}><Tooltip title="Xoá plan"><Button icon={<DeleteOutlined />} size="small" danger loading={deleting === p.id} aria-label="Xoá plan" /></Tooltip></span>
                      </Popconfirm>
                    </div>
                    <div className="prog"><div className="sb-bar"><i className={p.status === 'VIDEO_PENDING' ? 'gen' : ''} style={{ width: `${m.pct}%` }} /></div></div>
                  </div>
                  <div className="pbody">
                    <div className="ptitle">{p.name || 'Untitled'}</div>
                    <div className="plog">{p.logline || <span style={{ color: 'var(--sb-faint)' }}>No logline yet</span>}</div>
                    <div className="pmeta">
                      {(p.sourceChapterIds || []).slice(0, 3).map((id) => <span key={id} className="sb-pchip ch">{chapterLabel(id)}</span>)}
                      {p.videoProvider && <span className="sb-pchip">{p.videoProvider === 'SEEDANCE' ? 'Seedance' : p.videoProvider}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <Modal title={<Space><VideoCameraOutlined />{CREATE_PLAN_LABELS.title}</Space>}
        open={modalOpen} onOk={onCreate} onCancel={() => setModalOpen(false)}
        okText={CREATE_PLAN_LABELS.okText} cancelText={CREATE_PLAN_LABELS.cancelText} confirmLoading={generating} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={CREATE_PLAN_LABELS.name}>
            <Input placeholder={CREATE_PLAN_LABELS.namePlaceholder} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="fromChapterId" label={CREATE_PLAN_LABELS.fromChapter} rules={[{ required: true, message: CREATE_PLAN_LABELS.fromRequired }]}>
                <Select placeholder={CREATE_PLAN_LABELS.fromPlaceholder} optionFilterProp="label" getPopupContainer={inField}
                  onChange={() => form.setFieldValue('toChapterId', undefined)} options={fromOptions} data-testid="film-create-from" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toChapterId" label={CREATE_PLAN_LABELS.toChapter} rules={[{ required: true, message: CREATE_PLAN_LABELS.toRequired }]}>
                <Select placeholder={CREATE_PLAN_LABELS.toPlaceholder} optionFilterProp="label" getPopupContainer={inField} options={toOptions} data-testid="film-create-to" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="aestheticRef" label={CREATE_PLAN_LABELS.aesthetic}>
            <Input placeholder={CREATE_PLAN_LABELS.aestheticPlaceholder} />
          </Form.Item>
          {/* 261010 P1a/P7c — luồng mới (Sky): mặc định BẬT; tắt tay ⇒ plan chia y như trước. Reset form ⇒ về BẬT (mỗi lần tạo là một lựa chọn). */}
          <Form.Item name="skyFlow" valuePropName="checked" initialValue={SKY_FLOW_DEFAULT}
            label={<Tooltip title={SKY_TOGGLE_TOOLTIP}><span>Luồng mới ⓘ</span></Tooltip>}>
            <Switch />
          </Form.Item>
          {/* 261010 UI2 (L2) — engine lúc Tạo plan: CHỈ đi cùng "Luồng mới" BẬT (BE: generation ngoài luồng mới ⇒ 400). 2.0 chọn model + độ phân giải; 2.5 chỉ độ phân giải. Không hiện giá. */}
          <div data-testid="film-create-engine" style={{ opacity: watchedSky ? 1 : 0.5 }}>
            <Form.Item name="genEngine" label="Engine" initialValue={DEFAULT_GENERATION_FORM.engine} style={{ marginBottom: 8 }}
              extra={watchedSky ? undefined : 'Bật "Luồng mới" để chọn engine ngay lúc tạo plan.'}>
              <Select disabled={!watchedSky} options={GENERATION_ENGINES.map((e) => ({ value: e.value, label: e.label }))}
                onChange={(v) => { const fx = generationFormFixOf({ engine: v, model: form.getFieldValue('genModel'), resolution: form.getFieldValue('genResolution') }); form.setFieldsValue({ genModel: fx.model ?? undefined, genResolution: fx.resolution ?? undefined }); }} />
            </Form.Item>
            <Row gutter={12}>
              {watchedEngine !== 'SEEDANCE_25' ? (
                <Col span={12}>
                  <Form.Item name="genModel" label="Model (2.0)" initialValue="mini" style={{ marginBottom: 8 }}>
                    <Select disabled={!watchedSky} options={GENERATION_MODELS_20.map((m) => ({ value: m.value, label: m.label }))} />
                  </Form.Item>
                </Col>
              ) : null}
              <Col span={watchedEngine === 'SEEDANCE_25' ? 24 : 12}>
                <Form.Item name="genResolution" label="Độ phân giải" initialValue="480p" style={{ marginBottom: 8 }}
                  extra={watchedEngine === 'SEEDANCE_25' ? 'Seedance 2.5 chưa có lựa chọn model — chỉ chọn độ phân giải.' : undefined}>
                  <Select disabled={!watchedSky} options={GENERATION_RESOLUTIONS.map((r) => ({ value: r.value, label: r.label }))} />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>{CREATE_PLAN_LABELS.usedNote}</Text>
        </Form>
      </Modal>
    </div>
  );
};

// ── Plan Detail (inner tabs) ──
const PlanDetail: React.FC<{ plan: FilmPlan; storyId: string; onBack: () => void; focusEventId?: string }> = ({ plan, storyId, onBack, focusEventId }) => {
  const navigate = useNavigate();
  // Workspace 1 màn: header plan+rail gộp trong EventPlanTab; storyboard editor. Bank/Assemble mở qua drawer.
  return (
    <div className="sb-scope">
      <EventPlanTab planId={plan.id} onBack={onBack} planName={plan.name || 'Untitled'} planStatus={plan.status} chapterIds={plan.sourceChapterIds || []} focusEventId={focusEventId}
        onOpenWorkflow={() => navigate(workflowPath(storyId, plan.id))} />
    </div>
  );
};

// ── Film Area Inner ──
const FilmAreaInner: React.FC<{ storyId: string; active?: boolean; onPlanOpenChange?: (open: boolean) => void }> = ({ storyId, active, onPlanOpenChange }) => {
  const { plan, plans, selectPlan } = useFilmPlan();
  // Bug1 fix: NHỚ plan đang mở trong URL (?fplan=) → reload/back của trình duyệt KHÔNG bay ra list.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('fplan') || undefined;

  const setOpenParam = useCallback((id?: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('fplan', id); else { next.delete('fplan'); next.delete('fevent'); }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /**
   * 🔴 260805 — URL LÀ NGUỒN CHÂN LÝ DUY NHẤT cho "đang mở plan nào".
   *
   * Bản cũ giữ thêm state `selectedPlan` song song với `?fplan=`, và có một effect "chưa mở mà URL có id
   * thì mở lên". Hai nguồn đó lệch pha nhau: `setSelectedPlan(null)` áp dụng NGAY, còn `setOpenParam`
   * qua router nên chậm một nhịp ⇒ ở lượt render giữa, effect thấy `!selectedPlan && openId` và MỞ LẠI
   * plan vừa đóng. Hệ quả: bấm "Plans" lần đầu chỉ xoá param (URL sạch) mà màn vẫn ở trong plan, phải
   * bấm lần hai mới ra được list.
   *
   * Suy thẳng từ `openId` thì không còn hai nguồn để lệch. Ưu tiên bản đầy đủ từ context (name/status mới
   * nhất), fallback về bản trong list khi context chưa kịp load.
   */
  const selectedPlan = useMemo<FilmPlan | null>(() => {
    if (!openId) return null;
    if (plan && plan.id === openId) return plan;
    return plans.find((p) => p.id === openId) ?? null;
  }, [openId, plan, plans]);

  // Context bám theo URL (đọc plan đầy đủ khi mở, dọn khi đóng).
  useEffect(() => { selectPlan(openId); }, [openId, selectPlan]);

  // URL trỏ plan không tồn tại (đã xoá ở nơi khác / vừa xoá) → dọn param, không kẹt màn trống.
  useEffect(() => {
    if (openId && plans.length && !plans.some((p) => p.id === openId)) setOpenParam(undefined);
  }, [openId, plans, setOpenParam]);

  // Báo lên cha: đang mở 1 plan hay không → cha ẩn toggle/tabs khi trong plan (bớt tầng nav).
  useEffect(() => { onPlanOpenChange?.(!!selectedPlan); }, [selectedPlan, onPlanOpenChange]);

  // Plan LUỒNG MỚI ⇒ khung mới (đường workflow); plan cũ ⇒ UI cũ qua ?fplan=. `?fplan=` không tự chuyển hướng (đó là đường của nút «Mở UI cũ» trong khung mới).
  const navigate = useNavigate();
  const handleSelectPlan = (p: FilmPlan) => {
    const t = planOpenTargetOf(storyId, p);
    if (t.kind === 'FOUR') navigate(t.path); else setOpenParam(t.planId);
  };
  const handleBack = () => setOpenParam(undefined);

  // Bỏ Card box full-width (gây lệch: nội dung cap-trái trong hộp full-width) → content ngồi thẳng trên nền trang.
  return selectedPlan
    ? <PlanDetail plan={selectedPlan} storyId={storyId} onBack={handleBack} focusEventId={searchParams.get('fevent') || undefined} />
    : <PlanList storyId={storyId} active={active} onSelect={handleSelectPlan} onCreateFour={() => navigate(`/source-stories/${storyId}/film/create-four`)} />;
};

interface Props { storyId: string; active?: boolean; onPlanOpenChange?: (open: boolean) => void; }

const FilmArea: React.FC<Props> = ({ storyId, active, onPlanOpenChange }) => (
  <FilmPlanProvider storyId={storyId}>
    <FilmAreaInner storyId={storyId} active={active} onPlanOpenChange={onPlanOpenChange} />
  </FilmPlanProvider>
);

export default FilmArea;
